import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Printer, X } from 'lucide-react';
import axios from 'axios';
import NavigationBar from '../components/NavigationBar';
import PatientTabs from '../components/PatientTabs';
import usePatientSummary from '../utils/usePatientSummary';
import useFrail from '../utils/useFrail';
import FrailAnswersSection from '../components/FrailAnswersSection';
import '../css/Survey.css';
import { API_BASE_URL } from '../config/config';

// 답변이 없을 때 서버가 보내는 글자. 빈 값이 아니라 이 문자열로 온다 —
// src/utils/surveyMapper.js 의 getSurveyEnumValue 가 null·undefined·빈 문자열을
// { desc: '답변 없음' } 으로 바꾼다. 그래서 "답이 비었나"를 falsy 로만 보면 못 잡는다.
// 저 파일의 문구를 바꾸면 여기도 함께 바꿀 것.
const NO_ANSWER = '답변 없음';
const isEmptyAnswer = (value) => !value || String(value).trim() === NO_ANSWER;

const Survey = ({ userId: propUserId, userName: propUserName, onClose }) => {
    console.log('[Survey] Component Rendered, propUserId:', propUserId);
    console.log('[Survey] Component Rendered, propUserName:', propUserName);
    const { userId: paramUserId } = useParams();
    const { userName: paramUserName } = useParams();

    const userId = propUserId || paramUserId;
    // 주소로 바로 열면 이름을 모른다. 서버에 물어 머리말과 스마트링 탭을 채운다.
    const summary = usePatientSummary(userId);
    const userName = propUserName || paramUserName || summary?.name;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [userSurveyData, setUserSurveyData] = useState(null);
    // 프레일 평가는 가입 설문과 다른 표(sdss_hd.frail_assessment)에서 온다. 가입 설문이 없어도 따로 보인다.
    const frail = useFrail(userId);

    useEffect(() => {
        console.log('[Survey] useEffect triggered for userId:', userId);
        if (!userId) {
            console.log('[Survey] userId is missing, skipping fetch');
            return;
        }

        const fetchUserData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const url = `${API_BASE_URL}/api/survey/${userId}`;
                console.log('[Survey] Fetching from URL:', url);
                const response = await axios.get(url, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (response.status == 200 && response.data.success) {
                    setUserSurveyData(response.data.data || { name: 'Unknown User' });
                } else {
                    setUserSurveyData({ name: 'Unknown User' });
                }
            } catch (error) {
                console.error('Error fetching user data for survey:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchUserData();
    }, [userId]);

    // 이중 진입 화면 셋(케어플랜·설문·보고서)이 같은 식으로 센다.
    const isModal = typeof onClose === 'function';

    const handlePrint = () => {
        const printContent = document.getElementById('printable-survey-area');
        if (!printContent) return;

        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);

        // ⚠️ 아래 <style> 의 글자색 리터럴(h1…div 규칙)은 토큰으로 바꾸지 말 것. 이 iframe 은 앱 번들을 읽지
        //    않으므로 :root 의 커스텀 프로퍼티가 존재하지 않는다 — var(--color-text) 를
        //    넣으면 값이 없어 규칙이 조용히 죽고, 인쇄물 글자색이 브라우저 기본값이 된다.
        //    캐스케이드 바깥에 있어서 값을 하는 리터럴이다(스마트링 화면의 수면 카드와 같은 부류).
        //    같은 이유로 .no-print 와 .page-break-avoid 규칙도 여기에만 있다. 번들 CSS 에는
        //    그 이름이 없고, 있어도 이 iframe 에는 닿지 않는다.
        iframe.contentDocument.write(`
            <html>
                <head>
                    <title>설문조사 결과지</title>
                    <style>
                        body {
                            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
                            background-color: white;
                            margin: 0;
                            padding: 20px;
                        }
                        .no-print { display: none !important; }
                        .page-break-avoid { page-break-inside: avoid; break-inside: avoid; }
                        h1, h2, h3, h4, h5, p, span, div { color: #333; }
                    </style>
                </head>
                <body>
                    <div style="max-width: 800px; margin: 0 auto;">
                        ${printContent.innerHTML}
                    </div>
                </body>
            </html>
        `);
        iframe.contentDocument.close();

        setTimeout(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 1000);
        }, 500); // Wait for rendering
    };

    // 응답이 와도 카테고리 배열이 전부 비어 있을 수 있다(설문을 아직 안 쓴 대상자).
    // 그때는 빈 카드 더미가 아니라 안내 한 줄을 그린다.
    const hasAnswers = Boolean(userSurveyData) && Object.entries(userSurveyData).some(
        ([category, items]) => category !== 'name' && Array.isArray(items) && items.length > 0
    );

    return (
        <div className={`survey-page${isModal ? ' survey-page--modal' : ''}`}>
            {/* ⚠️ 상단 바는 .sds-page 의 바로 앞 형제여야 한다. NavigationBar.css 153행의
                `.navigation-bar + *` 가 아래 내용의 위 여백(64+24)을 주는데, 감싸는 요소를
                하나라도 끼우면 인접 선택자가 끊긴다. 예전에는 여기에 .no-print 로 감싼
                div 가 있어서 그 여백을 손으로 80px 로 맞추고 있었다 — 그 감싸개는
                인쇄에 아무 일도 하지 않는다. 인쇄용 iframe 은 #printable-survey-area 의
                innerHTML 만 받아 가는데 상단 바는 그 바깥에 있고, 번들 CSS 어디에도
                .no-print 규칙이 없기 때문이다(규칙은 handlePrint 가 쓰는 <style> 안에만 있다).
                모달로 열리면 대시보드 안이라 그리지 않는다 — 그때는 이 규칙도 걸리지 않는다. */}
            {!isModal && <NavigationBar />}
            {/* 다른 대상자 화면과 같은 껍데기다 — 회색 바탕 위 .sds-page 폭, sds-page-header,
                탭 줄, 그 아래 흰 카드. 모달로 열리면 대시보드가 이미 흰 상자를 씌우므로
                바탕색과 바깥 여백만 survey-page--modal 로 걷는다. */}
            <div className="sds-page detail-page">
                {/* 뒤로가기는 헤더 오른쪽이 아니라 브레드크럼 자리다 — 개요·스마트링·케어플랜과 같다.
                    모달에는 돌아갈 히스토리가 없으므로 이 줄 대신 헤더의 닫기 버튼을 쓴다.
                    인쇄 영역 바깥이다 — 결과지에 "뒤로"가 찍힐 이유가 없다. */}
                {!isModal && (
                    <div className="detail-page__crumb">
                        <button className="sds-btn sds-btn--ghost sds-btn--sm" onClick={() => navigate(-1)}>
                            <ChevronLeft size={16} aria-hidden="true" />뒤로
                        </button>
                    </div>
                )}

                {/* 인쇄 영역은 예전과 같이 제목 줄부터 시작한다 — 결과지 맨 위에 누구의
                    무슨 서류인지가 찍혀야 하기 때문이다. 화면에만 있어야 하는 것(버튼 줄,
                    탭 줄)에는 .no-print 를 붙인다. 그 규칙은 handlePrint 의 <style> 이 준다. */}
                <div id="printable-survey-area">
                    <div className="sds-page-header">
                        <div>
                            {/* 제목은 대상자다. 어느 화면인지는 아래 탭 줄이 말한다. 이름은
                                /api/patients/:id/summary 로 받는다 — 못 받으면 id 로 부른다. */}
                            <h1 className="sds-page-header__title">
                                {userName || `대상자 ${userId}`}
                                <small>설문조사 결과지</small>
                            </h1>
                        </div>
                        <div className="sds-page-header__actions no-print">
                            <button className="sds-btn sds-btn--secondary" onClick={handlePrint}>
                                <Printer size={16} aria-hidden="true" />인쇄
                            </button>
                            {isModal && (
                                <button className="sds-btn sds-btn--ghost" onClick={onClose}>
                                    <X size={16} aria-hidden="true" />닫기
                                </button>
                            )}
                        </div>
                    </div>

                    {/* 모달 안에서는 탭 줄을 그리지 않는다. 모달이 닫히지 않은 채 주소만 바뀐다.
                        감싸개는 인쇄에서 빼려는 것이다 — PatientTabs 는 className 을 받지 않는다.
                        상단 바와 달리 여기는 인접 선택자에 걸린 것이 없어 감싸도 안전하다. */}
                    {!isModal && (
                        <div className="no-print">
                            <PatientTabs appUserId={userId} active="survey" hasSmartRing={summary?.hasSmartRing === true} />
                        </div>
                    )}

                    {loading ? (
                        <div className="sds-notice survey-page__notice">설문 결과를 불러오는 중입니다.</div>
                    ) : !hasAnswers ? (
                        <>
                            <div className="sds-notice survey-page__notice">사용자 설문이 아직 작성되지 않았습니다.</div>
                            <FrailAnswersSection assessment={frail.data?.assessment || null} loading={frail.loading} error={frail.error} />
                        </>
                    ) : (
                        <>
                            <section className="sds-list survey-page__card">
                                <div className="sds-list__row">
                                    <div className="sds-list__body"><b>사용자</b></div>
                                    <div className="sds-list__spacer" />
                                    <div className="sds-list__value">{userName || '-'}</div>
                                </div>
                                <div className="sds-list__row">
                                    <div className="sds-list__body"><b>사용자 ID</b></div>
                                    <div className="sds-list__spacer" />
                                    <div className="sds-list__value">{userId}</div>
                                </div>
                            </section>

                            {Object.entries(userSurveyData).map(([category, items]) => (
                                category !== 'name' && Array.isArray(items) && items.length > 0 && (
                                    <section key={category} className="sds-list survey-page__card">
                                        <div className="sds-list__title">{items[0].categoryName || category}</div>
                                        {items.map((item, idx) => (
                                            /* page-break-avoid 는 인쇄에서 질문과 답변이 쪽을 넘어
                                               갈라지지 않게 한다. 예전과 같은 자리다. */
                                            <div key={`${category}-${idx}`} className="sds-list__row page-break-avoid">
                                                <div className="sds-list__body">
                                                    <b>{item.questionStr || `Question ${item.questionId}`}</b>
                                                </div>
                                                <div className="sds-list__spacer" />
                                                <div className={`sds-list__value survey-page__answer${isEmptyAnswer(item.answerStr) ? ' survey-page__answer--empty' : ''}`}>
                                                    {item.answerStr || NO_ANSWER}
                                                </div>
                                            </div>
                                        ))}
                                    </section>
                                )
                            ))}

                            {/* 프레일 평가(7문항). 가입 설문 영역들 다음, 결과지 맨 끝. */}
                            <FrailAnswersSection assessment={frail.data?.assessment || null} loading={frail.loading} error={frail.error} />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Survey;
